import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    ViewChild
} from '@angular/core';
import { delay, map, switchMap, take, tap, throwIfEmpty } from 'rxjs/operators';
import { SettingsService } from '@core/services/settings.service';
import { LoadScriptsService } from '@core/services/load-scripts/load-scripts.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, throwError } from 'rxjs';
import { CmsSettingsViewModel } from '@models/api.models';
import { LoggingService } from '@core/services/logging.service';
import { PlatformService } from '@core/services/platform.service';
import {
    AnydayPriceTag,
    AnydayPriceTagHeading,
    AnydayPriceTagMessage,
    AnydayPriceTagOrientation,
    AnydayPriceTagSize,
    AnydayPriceTagTheme
} from '@features/product/product.models';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { NgIf } from '@angular/common';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

@UntilDestroy()
@Component({
    selector: 'app-product-price-anyday',
    templateUrl: './product-price-anyday.component.html',
    styleUrls: ['./product-price-anyday.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, LoadingDotsComponent]
})
export class ProductPriceAnydayComponent implements AfterViewInit {
    private isLarge: boolean = false;

    public isFetchingAnydayData: boolean = true;

    @HostBinding('class.product-price-anyday--large') get isLargeClass(): boolean {
        return this.isLarge;
    }

    @ViewChild('anydayPriceHolderEl') anydayPriceHolderEl: ElementRef;

    @Input() unitPrice: string;

    constructor(
        private settingsService: SettingsService,
        private loadScriptService: LoadScriptsService,
        private loggingService: LoggingService,
        private plataformService: PlatformService,
        private el: ElementRef,
        private cd: ChangeDetectorRef
    ) {}

    public ngAfterViewInit(): void {
        // we don't want load the anyday price tag on SSR
        if (this.plataformService.isBrowser) {
            this.loadAnydayPriceTag();
        }
    }

    private loadAnydayPriceTag(): void {
        this.settingsService.settings$
            .pipe(
                switchMap(settings => this.loadScriptOrEmpty(settings)),
                tap(({ ecommerceSettings, anydaySettings }) => {
                    const { currencyCode = 'DKK' } = ecommerceSettings || {};
                    const {
                        priceTagToken = '',
                        anydaySize = 'small',
                        anydayTheme = 'outline',
                        anydayOrientation = 'logo-left',
                        anydayHeading = 1,
                        anydayMessage = 1,
                        anydayShopName = ''
                    } = anydaySettings || {};

                    this.isLarge = anydaySize === 'large';

                    this.createPriceTagElement({
                        size: anydaySize as AnydayPriceTagSize,
                        theme: anydayTheme as AnydayPriceTagTheme,
                        orientation: anydayOrientation as AnydayPriceTagOrientation,
                        heading: anydayHeading as AnydayPriceTagHeading,
                        message: anydayMessage as AnydayPriceTagMessage,
                        currency: currencyCode,
                        fullyClickable: false,
                        shopName: anydayShopName,
                        locale: 'da-DK',
                        price: this.unitPrice,
                        token: priceTagToken
                    });
                }),
                throwIfEmpty(),
                // the delay was added because when the element is rendered in the dom take a while until show their contents
                delay(200),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(
                _ => {},
                error => {
                    // this message errors are only for internal control
                    this.loggingService.logMessage('Anyday price tag', error);

                    // since there are missing elements to execute the anyday price tag, we will remove the component it's self
                    // NOTE: the entire component will not be destroyed
                    this.el.nativeElement.remove();
                },
                () => {
                    this.isFetchingAnydayData = false;
                    this.cd.markForCheck();
                }
            );
    }

    private createPriceTagElement({
        size = 'small',
        theme = 'light',
        orientation = 'logo-left',
        heading = 1,
        message = 1,
        currency = 'DKK',
        priceFormatLocale = 'da-DK',
        fullyClickable = false,
        shopName = '',
        locale = 'da-DK',
        containerBreakpoint = '',
        price = '',
        token
    }: AnydayPriceTag): void {
        const anydayEl: HTMLElement = this.anydayPriceHolderEl.nativeElement;
        const priceTagEl: HTMLElement = document.createElement('anyday-price-widget');
        priceTagEl.setAttribute('size', size);
        priceTagEl.setAttribute('theme', theme);
        priceTagEl.setAttribute('orientation', orientation);
        priceTagEl.setAttribute('heading', `${heading}`);
        priceTagEl.setAttribute('message', `${message}`);
        priceTagEl.setAttribute('currency', currency);
        priceTagEl.setAttribute('price', price.toString());
        priceTagEl.setAttribute('total-price', price.toString());
        priceTagEl.setAttribute('price-format-locale', priceFormatLocale);
        priceTagEl.setAttribute('locale', locale);
        priceTagEl.setAttribute('fully-clickable', `${fullyClickable}`);
        priceTagEl.setAttribute('container-breakpoint', containerBreakpoint);
        priceTagEl.setAttribute('shop-name', shopName);
        priceTagEl.setAttribute('token', token);
        anydayEl.appendChild(priceTagEl);
    }

    private loadScriptOrEmpty(settings: CmsSettingsViewModel): Observable<CmsSettingsViewModel> {
        const { anydaySettings } = settings;
        const { scriptURL, priceTagToken } = anydaySettings || {};

        // this message errors are only for internal control
        if (!scriptURL) {
            return throwError(`it's missing the script url to load the anyday plugin`);
        }

        if (!priceTagToken) {
            return throwError(`it's missing the priceTagToken to load the anyday plugin`);
        }

        return fromPromise<boolean>(
            this.loadScriptService.loadScript(scriptURL, { async: true, position: 'beforeBodyEnd' })
        ).pipe(map(() => settings));
    }
}
